import * as React from "react";
import classNames from "classnames";
import { Dispatch } from "redux";
import { connect } from "react-redux";
import { DatePicker as MUIDatePicker } from "material-ui-pickers";
import { translate, InjectedTranslateProps } from "react-i18next";
import { startOfMonth, endOfMonth, addMonths, subMonths } from "date-fns";
import { WithStyles, Theme, createStyles, withStyles } from "@material-ui/core";
import { getOccupiedDaysByRange, OccupiedDay } from "../../api/occupied-days";
import { StoreState } from "../../reducers/root-reducer";
import { updateOccupiedDays } from "../../actions/store/app-actions";
import { DatePickerWrapperProps } from "../../../node_modules/material-ui-pickers/DatePicker/DatePickerWrapper";

export interface DatePickerProps {
	value: Date;
	onChange: (date: Date) => void;
	onRef?: (instance: DatePicker) => void;
	datepickerProps?: Partial<DatePickerWrapperProps>;
}

interface ConntectedDatePickerProps {
	selectedDate: Date;
	occupiedDays: OccupiedDay[];
	updateOccupiedDays: (occupiedDays: OccupiedDay[]) => void;
}

type Props = DatePickerProps &
	ConntectedDatePickerProps &
	InjectedTranslateProps &
	WithStyles<typeof styles>;

export class DatePicker extends React.Component<Props, {}> {
	private inputRef: HTMLInputElement = null;

	public async componentDidMount() {
		const { onRef } = this.props;

		onRef && onRef(this);

		await this.getOccupiedDaysForMonth(this.props.selectedDate);
	}

	private getOccupiedDaysForMonth = async (date: Date) => {
		const { updateOccupiedDays } = this.props;

		try {
			const from = startOfMonth(date);
			const to = endOfMonth(date);

			const occupiedDays = await getOccupiedDaysByRange(from, to);
			updateOccupiedDays(occupiedDays);
		} catch (e) {
			console.error(e);
		} 
	};

	public open = async () => {
		await this.getOccupiedDaysForMonth(this.props.selectedDate);

		this.inputRef.click();
	};

	private handleInputRef = (ref: HTMLInputElement) => {
		this.inputRef = ref;
	};

	private handleDialogShown = () => {
		const { selectedDate, classes } = this.props;
		const modalClass = classes.datepickerModal;

		const prevButton = document.querySelector(
			`.${modalClass} > div > div > div > div > button:first-child`
		) as HTMLButtonElement;
		const nextButton = document.querySelector(
			`.${modalClass} > div > div > div > div > button:last-child`
		) as HTMLButtonElement;

		let p = false;
		let n = false;
		let monthOffset = 0;

		prevButton.addEventListener("click", async event => {
			if (!p) {
				event.stopPropagation();

				try {
					monthOffset--;
					await this.getOccupiedDaysForMonth(
						subMonths(selectedDate, monthOffset * -1)
					);
				} catch (e) {}

				p = true;
				prevButton.click();
			} else {
				p = false;
			}
		});

		nextButton.addEventListener("click", async event => {
			if (!n) {
				event.stopPropagation();

				try {
					monthOffset++;
					await this.getOccupiedDaysForMonth(
						addMonths(selectedDate, monthOffset)
					);
				} catch (e) {}

				n = true;
				nextButton.click();
			} else {
				n = false;
			}
		});
	};

	private handleChange = (date: Date) => {
		this.props.onChange(date ? date : new Date());
	};

	private renderDay = (
		day: Date,
		selectedDate: Date,
		dayInCurrentMonth: boolean,
		dayComponent: React.ReactElement<{}>
	) => {
		const { classes, occupiedDays } = this.props;

		let hasReservation = false;
		let hasUnassignedReservation = false;

		occupiedDays.forEach(occupiedDay => { 
			if (day.getTime() === occupiedDay.date.getTime()) {
				if (occupiedDay.assignedReservations > 0) {
					hasReservation = true;
				} 
				if (occupiedDay.unassignedReservations > 0) {
					hasUnassignedReservation = true;
				}
			}
		});

		return (
			<div className={classes.dayWrapper}>
				{dayComponent}
				{!(dayComponent.props as any).hidden && (
					<div
						className={classNames(classes.indicator, {
							[classes.indicatorVisible]:
								hasReservation || hasUnassignedReservation,
							[classes.indicatorHasUnassigned]: hasUnassignedReservation
						})}
					/>
				)}
			</div>
		);
	};

	public render() {
		const { classes, value, onChange, t, datepickerProps } = this.props;

		return (
			<MUIDatePicker
				autoOk
				clearable
				clearLabel={t("datepicker.clear")}
				okLabel={t("datepicker.ok")}
				cancelLabel={t("datepicker.cancel")}
				inputRef={this.handleInputRef}
				onChange={this.handleChange}
				renderDay={this.renderDay}
				value={value}
				DialogProps={{
					className: classes.datepickerModal,
					onEntered: this.handleDialogShown
				}}
				{...datepickerProps}
			/>
		);
	}
}

const mapStateToProps = (state: StoreState) => ({
	selectedDate: state.date,
	occupiedDays: state.occupiedDays
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	updateOccupiedDays: (occupiedDays: OccupiedDay[]) =>
		dispatch(updateOccupiedDays(occupiedDays))
});

const styles = (theme: Theme) =>
	createStyles({
		dayWrapper: {
			position: "relative"
		},
		indicator: {
			display: "none",
			position: "absolute",
			bottom: 0,
			left: "50%",
			width: 5,
			height: 5,
			borderRadius: "100%",
			backgroundColor: "green",
			transform: "translate3d(-50%, 0, 0)"
		},
		indicatorVisible: {
			display: "block"
		},
		indicatorHasUnassigned: {
			backgroundColor: "red"
		},
		datepickerModal: {}
	});

export const ConnectedDatePicker = translate("translations")(
	withStyles(styles)(
		connect(
			mapStateToProps,
			mapDispatchToProps
		)(DatePicker)
	)
);
